import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { IdNameList } from "../../../../api/services/common/models";
import { FormController, MuiGrid, MuiSelect } from "../../../../components";
import { gridSize, MODULE_PERMISSIONS } from "../../../../helpers/constants";
import { setApiDataToDropDownOption } from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { selectPendingBillingFormKeys } from "../../modelsAndSchemas/models";
import { selectReportSiteCoreKeys } from "../../siteCore/SiteCoreKeys";

interface ReportSearchFormControllerProps {
  reportCategories: IdNameList[];
  reportTypes: IdNameList[];
  handleCategoryChange: (categoryId: string) => void;
}

const BILLING_OPTION_VALUE = "Billing Related List";
const COACH_OPTION_VALUE = "Coach Related Lists";
const OUTREACH_OPTION_VALUE = "Outreach Related Lists";

export const ReportSearchFormController = ({
  reportCategories,
  reportTypes,
  handleCategoryChange,
}: ReportSearchFormControllerProps) => {
  const { control } = useFormContext();

  const { useFetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();

  const reportCategoriesDropdown = useMemo(() => {
    if (!reportCategories || reportCategories.length === 0) {
      return [];
    }
    let filteredReportCategories: IdNameList[] | undefined = reportCategories;
    if (!hasModulePermission(MODULE_PERMISSIONS.REPORT_BILLING)) {
      filteredReportCategories = filteredReportCategories?.filter(
        (category) =>
          !category.name
            .toLowerCase()
            .includes(BILLING_OPTION_VALUE.toLowerCase()),
      );
    }
    if (!hasModulePermission(MODULE_PERMISSIONS.REPORT_COACH)) {
      filteredReportCategories = filteredReportCategories?.filter(
        (category) =>
          !category.name
            .toLowerCase()
            .includes(COACH_OPTION_VALUE.toLowerCase()),
      );
    }
    if (!hasModulePermission(MODULE_PERMISSIONS.REPORT_OUTREACH)) {
      filteredReportCategories = filteredReportCategories?.filter(
        (category) =>
          !category.name
            .toLowerCase()
            .includes(OUTREACH_OPTION_VALUE.toLowerCase()),
      );
    }

    return setApiDataToDropDownOption(filteredReportCategories);
  }, [reportCategories, hasModulePermission]);

  const reportTypesDropdown = useMemo(() => {
    if (!reportTypes || reportTypes.length === 0) {
      return [];
    }
    return setApiDataToDropDownOption(reportTypes);
  }, [reportTypes]);

  const getLabel = useFetchLabel(selectReportSiteCoreKeys.header);

  return (
    <>
      <MuiGrid size={gridSize}>
        <FormController
          control={control}
          name={selectPendingBillingFormKeys.categoryId}
        >
          <MuiSelect
            isMandatory
            label={getLabel(selectReportSiteCoreKeys.reportCategoryDropDown)}
            options={reportCategoriesDropdown}
            visibleDefaultSelectOption
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
            onChange={(e) => handleCategoryChange(e.target.value as string)}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize}>
        <FormController
          control={control}
          name={selectPendingBillingFormKeys.typeId}
        >
          <MuiSelect
            showSearch
            isMandatory
            label={getLabel(selectReportSiteCoreKeys.reportTypeDropDown)}
            options={reportTypesDropdown}
            visibleDefaultSelectOption
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
    </>
  );
};
